import React, { useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { Button } from 'react-bootstrap';

import { AngularContext } from 'react-app';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { useAppDispatch } from 'redux/store';
import { DisableProps } from './integrations';
import SectionContent from '../section-content';
import { config } from '../../../../../config/pendo.config.json';

interface WorkeraConfig {
  workeraWebhookSecret?: string;
  workeraWebhookUrl?: string;
}

const WorkeraIntegration = (props: { onDisable: (props: DisableProps) => void }) => {
  const { injectServices, $scope } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);
  const [hasEnabled, setHasEnabled] = useState($scope.$eval('vm.InstitutionsManager.institution.isWorkeraEnabled'));
  const [showModal, setShowModal] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [isToggling, setIsToggling] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    $scope.$watch('vm.InstitutionsManager.institution.isWorkeraEnabled', (newValue) => {
      setHasEnabled(newValue);
    });
  }, [$scope]);

  const enableWorkera = () => {
    setIsToggling(true);
    InstitutionsManager.enableWorkeraIntegration()
      .then((result: WorkeraConfig) => {
        setWebhookUrl(result?.workeraWebhookUrl || '');
        setWebhookSecret(result?.workeraWebhookSecret || '');
        setShowModal(true);
      })
      .catch(() => {
        dispatch(
          addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.FORM.FAILED(),
            message: t.FORM.ERROR_TRY_AGAIN(),
          }),
        );
      })
      .finally(() => {
        setIsToggling(false);
      });
  };

  const disableWorkera = () => {
    setIsToggling(true);
    props.onDisable({
      action: () => InstitutionsManager.disableWorkeraIntegration(),
      onSuccess: (response) => {
        setIsToggling(false);
        InstitutionsManager.institution.updateFromReact({
          isWorkeraEnabled: response?.isWorkeraEnabled,
        });
      },
      title: t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.DISABLE(),
      bodyText: t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.DISABLE_DESCRIPTION(),
      successMessage: t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.DISABLE_SUCCESS(),
      confirmDataQa: config.pendo.settings.workera.disableConfirm,
      cancelDataQa: config.pendo.settings.workera.disableCancel,
    });
  };

  const getCtaText = () => {
    if (isToggling) {
      return hasEnabled ? t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.DISABLING()
        : t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.ENABLING();
    }

    return hasEnabled
      ? t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.DISABLE()
      : t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.ENABLE();
  };

  const onCloseModal = () => {
    setShowModal(false);
    dispatch(
      addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: t.FORM.SUCCESS_BANG(),
        message: t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.ENABLE_SUCCESS(),
      }),
    );
  };

  return (
    <React.Fragment>
      <SectionContent
        header={t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.TITLE()}
        description={[t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.DESCRIPTION()]}
        showCTAButton
        ctaText={getCtaText()}
        onAction={() => (hasEnabled ? disableWorkera() : enableWorkera())}
        buttonVariant={hasEnabled ? 'secondary' : 'primary'}
        disabled={isToggling}
        dataQa={config.pendo.settings.workera.cta}
      />
      <NvModal
        show={showModal}
        height='auto'
        type={ModalType.FIXED}
        onClose={onCloseModal}
        header={t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.TITLE()}
        body={(
          <div className='text-body d-flex flex-column px-6 py-4'>
            <div className='mb-4'>
              {t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.MODAL.DESCRIPTION()}
            </div>
            <NvTextInput
              type='url'
              name='webhookUrl'
              withLabel
              labelAlwaysShown
              ariaLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.MODAL.WEBHOOK_URL()}
              label={t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.MODAL.WEBHOOK_URL()}
              className='mb-4'
              readOnly
              value={webhookUrl}
            />
            <NvTextInput
              type='text'
              name='webhookSecret'
              withLabel
              labelAlwaysShown
              ariaLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.MODAL.WEBHOOK_SECRET()}
              label={t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.MODAL.WEBHOOK_SECRET()}
              className='mb-4'
              readOnly
              value={webhookSecret}
            />
            <div>
              <span className='text-body font-weight-bolder mb-2 mr-1'>
                {t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.MODAL.PLEASE_NOTE()}
              </span>
              <span>
                {t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.MODAL.NOTE_1()}
              </span>
              <div>
                {t.INSTITUTIONS.ADVANCED_SETTINGS.WORKERA.MODAL.NOTE_2()}
              </div>
            </div>
            <div className='align-self-center mt-7'>
              <Button
                variant='primary'
                onClick={onCloseModal}
                data-qa={config.pendo.settings.workera.done}
              >
                {t.FORM.DONE()}
              </Button>
            </div>
          </div>
        )}
      />
    </React.Fragment>
  );
};
export default WorkeraIntegration;
